import React, { useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import ProgressBar from '../../common/ProgressBar';
import './styles.scss';
import {
    setSelectedChallenge,
    setSelectedChallengeId,
    challengeRequested,
    setParentChallenge,
    setSelectedCode,
} from './actions';
import { Marker, GoogleMap, Circle } from '@react-google-maps/api';
import PointerMarker from './../../icons/pointer.svg';
import BrowseIcon from './../../icons/browse.svg';
import { noop } from '../../common/propTypes';
import ConnectedTopTags from './ConnectedTopTags';
import ErrorBoundary from '../../common/ErrorBoundary';
import ConnectedTopContributors from './ConnectedTopContributors';
import ConnectedChallenges from './ConnectedChallenges';
import ChallengeCodes from './ChallengeCodes';
import ChallengeBanner from '../../common/ChallengeBanner';
import InfoItem from '../../common/InfoItem';
// import ConnectedMap from './ConnectedMap';
// import ConnectedImpactCard from './ConnectedImpactCard';
import SegmentsAndTags from './SegmentsAndTags';
import Notifications from './ConnectedNotifications';
import {
    getUser,
    getUserPermissionRoleId,
    getUserPermissions,
} from '../auth/selectors';
import {
    getChallenge,
    getChallengeLoading,
    getParentChallenge,
} from './selectors';
import LoadingComponent from '../../common/LoadingComponent';
import {
    CHALLENGE_TYPES,
    NOTIFICATION_PERMISSIONS,
} from '../../common/constants';
import usePrevious from '../../common/hooks/usePrevious';
import { ROUTE_CHALLENGE_CREATE } from '../../navigation/constants';
import moment from 'moment';

function Challenge({
    setChallenge,
    fetchChallenge,
    setChallengeId,
    selectedChallenge,
    challengeLoading,
    setParentChallenge,
    parentChallenge,
    user,
    permissions,
    setSelectedCode,
    currentRole,
}) {
    const [map, setMap] = React.useState(null);
    const [loading, setloading] = React.useState(true);
    // scroll top top
    const userPermissions = permissions.map((p) => p.key);
    const canSendNotification =
        selectedChallenge &&
        userPermissions.includes(
            NOTIFICATION_PERMISSIONS[selectedChallenge.badge],
        );
    const canCreateNewCodes =
        selectedChallenge &&
        userPermissions.includes('challenge_code_create') &&
        moment(selectedChallenge.endDate).diff(moment()) > 0;
    const canUpdateCodes =
        selectedChallenge &&
        userPermissions.includes('challenge_code_create') &&
        moment(selectedChallenge.startDate).diff(moment()) > 0 &&
        (selectedChallenge.partnerId === user.partnerId ||
            selectedChallenge.creatorId === user.id);

    useEffect(() => {
        window.scrollTo(0, 0);
        setParentChallenge();
        return () => {
            setParentChallenge();
        };
    }, []);

    const location = useLocation();
    const locationId = location.pathname.split('/')[2];
    const prevLocation = usePrevious(locationId);
    const isPartner = user && user.isPartner;
    const isResearcher = currentRole > 200 && currentRole < 300;
    const canEditChallenge = currentRole >= 200 && currentRole < 400;
    const amIcreator = user.id === selectedChallenge.creatorId;
    const hasChallengeExpired = moment(selectedChallenge.endDate).isBefore(
        moment(),
    );
    const { state } = location;
    const { from } = state || {};
    const locationFrom = from || null;

    useEffect(() => {
        if (!challengeLoading) {
            setloading(false);
        }
    }, [challengeLoading]);

    // run before the component mounts
    // the challengeId in state must be reset
    setChallengeId(locationId);

    useEffect(() => {
        setChallenge({});
        setSelectedCode('');
        if (location.state) {
            fetchChallenge(location.state.challenge.id);
        } else {
            fetchChallenge(locationId);
        }
    }, []);

    useEffect(() => {
        if (prevLocation && prevLocation !== locationId) {
            setChallenge({});
            fetchChallenge(locationId);
        }
    }, [location]);

    const navigation = [];
    navigation.push({ title: 'Challenges', path: '/challenges' });
    if (parentChallenge) {
        navigation.push({
            title: parentChallenge.title,
            path: `/challenges/${parentChallenge.id}`,
        });
    }
    navigation.push({ title: selectedChallenge.title, path: '#' });
    const long = selectedChallenge.location && selectedChallenge.location.long;
    const challengeLocation = { ...selectedChallenge.location, lng: long } || {
        lat: 30.20533080721662,
        lng: -97.74973791571,
    };
    const hasLocation =
        selectedChallenge.location && selectedChallenge.location.lat > 0;

    const onLoad = React.useCallback((map) => setMap(map), []);
    if (
        challengeLoading &&
        loading &&
        !selectedChallenge.id &&
        selectedChallenge.id != locationId
    ) {
        return <LoadingComponent isLoading={challengeLoading} />;
    }

    const mainContent = selectedChallenge ? (
        <div>
            <div className="row">
                <div className="col px-0">
                    <ChallengeBanner
                        id={locationId}
                        title={selectedChallenge.title}
                        image={selectedChallenge.banner}
                        iconClass="icon-flag"
                        creator={selectedChallenge.createdBy}
                        badge={selectedChallenge.badge}
                        navigation={navigation}
                        status={
                            selectedChallenge.isPrivate ? 'Private' : 'Public'
                        }
                        isChallenge
                        isChallengeBanner={
                            selectedChallenge.endDate &&
                            !hasChallengeExpired &&
                            canEditChallenge &&
                            (user.partnerId === selectedChallenge.partnerId ||
                                amIcreator)
                        }
                    />
                </div>
            </div>
            <div className="spacer"></div>
            <div className="row">
                <div className="col">
                    <div className="content-box card">
                        <div className="row">
                            <div className="col-md-7 col-sm-12">
                                <div className="row h-100">
                                    <div className="col-12 align-self-start">
                                        <div className="challengesWrapper pr-0 pr-md-5">
                                            <h3 className="titleStyle2">
                                                Challenge Description
                                            </h3>
                                            <p className="paraStyleTwo">
                                                {selectedChallenge.description}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="spacer-challenge"></div>
                                    <div className="col-12 d-flex">
                                        <div className="col-lg-6">
                                            <InfoItem
                                                heading={'Start Date'}
                                                subHeading={
                                                    selectedChallenge.startDate
                                                }
                                            />
                                        </div>
                                        <div className="col-lg-6">
                                            <InfoItem
                                                heading={'End Date'}
                                                subHeading={
                                                    selectedChallenge.endDate
                                                }
                                            />
                                        </div>
                                    </div>
                                    {selectedChallenge.website && (
                                        <>
                                            <div className="spacer-challenge"></div>
                                            <div className="col-12 d-flex">
                                                <div className="col-lg-6">
                                                    <InfoItem
                                                        heading={'Website'}
                                                        avatar={BrowseIcon}
                                                        isAvatar
                                                        subHeading={
                                                            selectedChallenge.website
                                                        }
                                                        hasLink
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className="spacer-challenge"></div>
                                    <div className="col-lg-12 align-self-end">
                                        <h3 className="titleStyle2">
                                            Pickup Quantity
                                        </h3>
                                        <h3 className="title-secondary progress-font">
                                            {selectedChallenge.pickups}
                                            <span className="progressData">
                                                /{selectedChallenge.target}
                                            </span>
                                        </h3>
                                        <ProgressBar
                                            progress={
                                                selectedChallenge.progress
                                            }
                                        />
                                    </div>
                                    <div className="spacer-challenge"></div>
                                    <div className="col-lg-12 align-self-end">
                                        <h3 className="titleStyle2">
                                            ACTIVE PARTICIPANTS
                                        </h3>
                                        <h3 className="title-secondary progress-font">
                                            {selectedChallenge.memberCount}
                                            <span className="progressData">
                                                /
                                                {selectedChallenge.memberTarget ||
                                                    '-'}
                                            </span>
                                        </h3>
                                        <ProgressBar
                                            progress={
                                                selectedChallenge.memberTarget
                                                    ? Math.round(
                                                          (selectedChallenge.memberCount /
                                                              selectedChallenge.memberTarget) *
                                                              100,
                                                      )
                                                    : 0
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-5 col-sm-12">
                                <div className="row">
                                    <div className="col-lg-12 align-self-end">
                                        <h3 className="titleStyle2">
                                            Location
                                        </h3>
                                        <GoogleMap
                                            id="showRadiusMap"
                                            options={{
                                                fullscreenControl: false,
                                                zoomControl: true,
                                                disableDoubleClickZoom: true,
                                                streetViewControl: false,
                                                mapTypeControl: false,
                                            }}
                                            map={map}
                                            onLoad={onLoad}
                                            zoom={hasLocation ? 10 : 1}
                                            center={challengeLocation}
                                        >
                                            {hasLocation &&
                                                (challengeLocation
                                                    ? [challengeLocation]
                                                    : []
                                                ).map((place) => (
                                                    <Fragment key={place.lat}>
                                                        <Marker
                                                            icon={PointerMarker}
                                                            position={place}
                                                        />
                                                        <Circle
                                                            center={place}
                                                            onLoad={(event) => {
                                                                // this is to fit bounds for circle
                                                                if (map) {
                                                                    map.fitBounds(
                                                                        event.getBounds(),
                                                                    );
                                                                }
                                                            }}
                                                            options={{
                                                                options: {
                                                                    strokeColor:
                                                                        'rgba(2, 122, 255)',
                                                                    strokeOpacity: 0.8,
                                                                    strokeWeight: 1,
                                                                    fillColor:
                                                                        'rgba(2, 122, 255)',
                                                                    fillOpacity: 0.35,
                                                                    clickable: false,
                                                                    draggable: false,
                                                                    editable: false,
                                                                    visible: true,
                                                                    zIndex: 1,
                                                                    radius:
                                                                        selectedChallenge.circleRadius,
                                                                },
                                                                radius:
                                                                    selectedChallenge.circleRadius,
                                                            }}
                                                        />
                                                    </Fragment>
                                                ))}
                                        </GoogleMap>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <LoadingComponent isLoading={challengeLoading} />
    );

    return (
        <div>
            {mainContent}
            <div className="spacer" />
            <div className="challengesWrapper">
                {locationFrom !== ROUTE_CHALLENGE_CREATE && (
                    <div className="row">
                        <div className="col-5">
                            <ErrorBoundary>
                                <ConnectedTopContributors />
                            </ErrorBoundary>
                        </div>
                        <div className="col-7 top-tags">
                            <ErrorBoundary>
                                <ConnectedTopTags />
                            </ErrorBoundary>
                        </div>
                    </div>
                )}
            </div>
            {// user.id === selectedChallenge.user &&
            (selectedChallenge.isMember || !selectedChallenge.isMember) &&
                (!locationFrom || locationFrom !== ROUTE_CHALLENGE_CREATE) && (
                    <>
                        <div className="spacer" />
                        <SegmentsAndTags isResearcher={isResearcher} />
                        <div className="spacer" />
                        {!challengeLoading &&
                        selectedChallenge &&
                        selectedChallenge.badge == CHALLENGE_TYPES.GLOBAL ? (
                            <ConnectedChallenges
                                challenge={selectedChallenge}
                            />
                        ) : null}
                        {isPartner && (
                            <>
                                <div className="spacer" />
                                <Notifications
                                    challenge={selectedChallenge}
                                    canSendNotification={canSendNotification}
                                />{' '}
                            </>
                        )}
                        <div className="spacer" />
                    </>
                )}
            <ChallengeCodes
                canUpdateCodes={canUpdateCodes}
                canCreateNewCodes={canCreateNewCodes}
            />
            <div className="spacer" />

            {/* {locationFrom !== ROUTE_CHALLENGE_CREATE && (
                <div className="row">
                    <div className="col-md-12 mapWrapper impactMap">
                        <ErrorBoundary>
                            <ConnectedMap />
                        </ErrorBoundary>
                        <div className="impact-container">
                            <ErrorBoundary>
                                <ConnectedImpactCard />
                            </ErrorBoundary>
                        </div>
                    </div>
                </div>
            )} */}
        </div>
    );
}

Challenge.propTypes = {
    // function to call before the component mounts
    // this sets the specific challenge id in state
    setChallengeId: PropTypes.func,
    setChallenge: PropTypes.func,
    // function to fetch the challenge if the user
    // did not navigate with challenge in state and
    // the challenge has to be fetched via API call
    fetchChallenge: PropTypes.func,
    setParentChallenge: PropTypes.func,
    selectedChallenge: PropTypes.object,
    parentChallenge: PropTypes.object,
    challengeLoading: PropTypes.bool,
    user: PropTypes.object,
    permissions: PropTypes.array,
    setSelectedCode: PropTypes.func,
    currentRole: PropTypes.number,
};

Challenge.defaultProps = {
    onLoad: noop,
    fetchChallenge: noop,
    selectedChallenge: null,
    challengeLoading: false,
    setSelectedCode: noop,
    currentRole: null,
};

const mapStateToProps = (state) => {
    return {
        selectedChallenge: getChallenge(state),
        challengeLoading: getChallengeLoading(state),
        parentChallenge: getParentChallenge(state),
        user: getUser(state),
        permissions: getUserPermissions(state),
        currentRole: getUserPermissionRoleId(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setChallengeId: (challengeId) =>
            dispatch(setSelectedChallengeId(challengeId)),
        setChallenge: (challenge) => dispatch(setSelectedChallenge(challenge)),
        fetchChallenge: (challengeId) =>
            dispatch(challengeRequested(challengeId)),
        setParentChallenge: () => dispatch(setParentChallenge(null)),
        setSelectedCode: (codeId) => dispatch(setSelectedCode(codeId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Challenge);
